import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatHash,
  formatError,
  isRecord,
  result,
  stableJson,
  validation,
  validationError,
  workerValue,
} from './shared.ts';

function realmIdentity(value: Navigator): RawData {
  return {
    userAgent: value.userAgent,
    platform: value.platform,
    language: value.language,
    cookieEnabled: value.cookieEnabled,
  };
}

function commonIdentity(identity: RawData): RawData {
  return {
    userAgent: identity.userAgent,
    platform: identity.platform,
    language: identity.language,
  };
}

async function collect(): Promise<RawData> {
  const mainIdentity = realmIdentity(navigator);
  const iframe = document.createElement('iframe');
  iframe.srcdoc = '<!doctype html><title>realm</title>';
  iframe.hidden = true;
  document.body.append(iframe);
  await new Promise<void>((resolve) => {
    iframe.addEventListener('load', () => resolve(), { once: true });
  });
  const iframeNavigator = iframe.contentWindow?.navigator;
  const iframeIdentity = iframeNavigator
    ? realmIdentity(iframeNavigator)
    : null;
  const iframeNavigatorKeys = iframeNavigator
    ? Object.getOwnPropertyNames(Object.getPrototypeOf(iframeNavigator)).sort()
    : null;
  iframe.remove();
  const workerData = await workerValue(
    '({ identity: { userAgent: self.navigator.userAgent, platform: self.navigator.platform, language: self.navigator.language }, navigatorKeys: Object.getOwnPropertyNames(Object.getPrototypeOf(self.navigator)).sort() })',
  );
  return {
    main: {
      identity: mainIdentity,
      navigatorKeys: Object.getOwnPropertyNames(Navigator.prototype).sort(),
    },
    iframe: iframeNavigator
      ? {
          identity: iframeIdentity,
          navigatorKeys: iframeNavigatorKeys,
        }
      : null,
    dedicatedWorker: isRecord(workerData) ? workerData : null,
  };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return formatHash(rawData);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  const main =
    isRecord(rawData.main) && isRecord(rawData.main.identity)
      ? rawData.main.identity
      : null;
  const iframe =
    isRecord(rawData.iframe) && isRecord(rawData.iframe.identity)
      ? rawData.iframe.identity
      : null;
  const dedicatedWorker =
    isRecord(rawData.dedicatedWorker) &&
    isRecord(rawData.dedicatedWorker.identity)
      ? rawData.dedicatedWorker.identity
      : null;
  if (!main || !iframe || !dedicatedWorker) {
    return validation(['Browser identity differs between realms']);
  }

  if (
    stableJson(commonIdentity(main)) != stableJson(commonIdentity(iframe)) ||
    stableJson(commonIdentity(main)) !=
      stableJson(commonIdentity(dedicatedWorker)) ||
    main.cookieEnabled != iframe.cookieEnabled
  ) {
    return validation(['Browser identity differs between realms']);
  }
  return validation([]);
}

export const crossRealmTest: TestDefinition = {
  id: 'crossRealmConsistency',
  title: 'Cross-realm consistency',
  category: 'shared',
  format,
  run,
  validate,
};
